import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { HomeRedirect } from './auth/HomeRedirect';
import { SessionProvider } from './auth/SessionProvider';
import { LanguageProvider } from './i18n/LanguageProvider';
import { BusinessShell } from './layout/BusinessShell';
import { PlatformShell } from './layout/PlatformShell';
import { BillingPage } from './pages/business/BillingPage';
import { BrandsPage } from './pages/business/BrandsPage';
import { CategoriesPage } from './pages/business/CategoriesPage';
import { CompaniesPage } from './pages/business/CompaniesPage';
import { ContentLanguagesPage } from './pages/business/ContentLanguagesPage';
import { FeatureGroupsPage } from './pages/business/FeatureGroupsPage';
import { IntegrationDetailPage } from './pages/business/IntegrationDetailPage';
import { IntegrationsPage } from './pages/business/IntegrationsPage';
import { MovementsPage } from './pages/business/MovementsPage';
import { OrderDetailPage } from './pages/business/OrderDetailPage';
import { OrdersPage } from './pages/business/OrdersPage';
import { ContactDetailPage } from './pages/business/ContactDetailPage';
import { ContactsPage } from './pages/business/ContactsPage';
import { OptionGroupsPage } from './pages/business/OptionGroupsPage';
import { OverviewPage } from './pages/business/OverviewPage';
import { ProductDetailPage } from './pages/business/ProductDetailPage';
import { ProductsPage } from './pages/business/ProductsPage';
import { TagsPage } from './pages/business/TagsPage';
import { TransfersPage } from './pages/business/TransfersPage';
import { UsersPage } from './pages/business/UsersPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { LoginPage } from './pages/LoginPage';
import { AuditPage } from './pages/platform/AuditPage';
import { IntegrationsPage as PlatformIntegrationsPage } from './pages/platform/IntegrationsPage';
import { InvoiceDetailPage } from './pages/platform/InvoiceDetailPage';
import { InvoicesPage } from './pages/platform/InvoicesPage';
import { SettingsPage } from './pages/platform/SettingsPage';
import { TenantDetailPage } from './pages/platform/TenantDetailPage';
import { TenantsPage } from './pages/platform/TenantsPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { ThemeModeProvider } from './theme/ThemeModeProvider';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 } },
});

/**
 * The route table. `/platform/*` belongs to super admins (admin-api), everything else to the users of one
 * business (business-api); each tree is guarded by the session kind it needs (Conventions/06 §2).
 */
export const App = () => (
  <ThemeModeProvider>
    <LanguageProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <SessionProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/password/forgot" element={<ForgotPasswordPage />} />
              <Route path="/password/set" element={<SetPasswordPage />} />

              <Route path="/platform" element={<PlatformShell />}>
                <Route index element={<Navigate to="/platform/tenants" replace />} />
                <Route path="tenants" element={<TenantsPage />} />
                <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="invoices/:invoiceId" element={<InvoiceDetailPage />} />
                <Route path="integrations" element={<PlatformIntegrationsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="audit" element={<AuditPage />} />
              </Route>

              <Route element={<BusinessShell />}>
                <Route path="/" element={<OverviewPage />} />
                <Route path="/companies" element={<CompaniesPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/:companyId/:productId" element={<ProductDetailPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/brands" element={<BrandsPage />} />
                <Route path="/tags" element={<TagsPage />} />
                <Route path="/features" element={<FeatureGroupsPage />} />
                <Route path="/option-groups" element={<OptionGroupsPage />} />
                <Route path="/movements" element={<MovementsPage />} />
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/contacts/:companyId/:contactId" element={<ContactDetailPage />} />
                <Route path="/orders/:companyId/:orderId" element={<OrderDetailPage />} />
                <Route path="/integrations" element={<IntegrationsPage />} />
                <Route path="/integrations/:companyId/:linkId" element={<IntegrationDetailPage />} />
                <Route path="/transfers" element={<TransfersPage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/settings/languages" element={<ContentLanguagesPage />} />
              </Route>

              <Route path="*" element={<HomeRedirect />} />
            </Routes>
          </SessionProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </LanguageProvider>
  </ThemeModeProvider>
);
